/**
 * Pure resolution of "which session should be active" for the filtered
 * (multi-session) chat provider. Extracted so the decision is one
 * order-independent, unit-testable function instead of a web of
 * `useEffect`s and `useState` snapshots — the previous design accreted a
 * deep-link sync effect, an adopt-existing effect, and a filter-reset
 * effect, each of which produced its own review finding because they
 * raced one another.
 *
 * The provider calls this once per render via `useMemo`, renders the
 * resolved session immediately (so deep-links and defaults show with no
 * flash), and runs exactly one effect that applies the side-effecting
 * resolutions (`adopt` pins the default, `create` mints a new session).
 *
 * Resolutions:
 * - `pending`  — list still loading, nothing usable yet. Render a spinner.
 * - `ready`    — `selectedId` matches a loaded session. Render it; no
 *                side effect (the selection is already pinned).
 * - `adopt`    — no valid selection but a default exists. Render it AND
 *                pin it (write the id back to the selection store) so a
 *                background list reorder can't swap the visible session.
 * - `create`   — no sessions and creation is allowed. Fire the create
 *                mutation.
 * - `missing`  — no sessions and creation is not allowed (or a prior
 *                create for this filter failed — the latch). Render the
 *                missing-session state.
 */
export type ChatSelectionResolution<T> =
  | { kind: "pending" }
  | { kind: "ready"; session: T }
  | { kind: "adopt"; session: T }
  | { kind: "create" }
  | { kind: "missing" };

export function resolveChatSelection<T extends { id: string; userId: string }>(input: {
  chatSessions: readonly T[];
  selectedId: string | null;
  currentUserId: string;
  allowCreateChatSession: boolean;
  loading: boolean;
  /**
   * Failure latch. Set once a `create` mutation for the current filter has
   * failed, so we resolve to `missing` instead of re-resolving to `create`.
   * Without this, a failed create leaves every input to this function
   * unchanged, so the provider's effect re-fires the mutation on the next
   * render — turning a single rejection (a 403, a 500, a network blip) into
   * an unbounded retry storm (B-1674). The provider clears it when the
   * filter changes, so a different conversation still gets one attempt.
   */
  createChatSessionFailed?: boolean;
}): ChatSelectionResolution<T> {
  const {
    chatSessions,
    selectedId,
    currentUserId,
    allowCreateChatSession,
    loading,
    createChatSessionFailed = false,
  } = input;

  // An explicit selection that resolves against the current list wins
  // outright — no side effect needed, it's already pinned. This covers
  // both a URL/controlled deep-link and a prior adopt that pinned itself.
  if (selectedId) {
    const match = chatSessions.find((session) => session.id === selectedId);
    if (match) {
      return { kind: "ready", session: match };
    }
  }

  // No usable explicit selection — compute the default to adopt. Prefer
  // the current user's own session (the list is ordered newest-first, so
  // `find` yields their most recent). Read-only consumers that can't
  // create fall back to the first session in the list so they still see
  // something rather than a dead end.
  const fallback =
    chatSessions.find((session) => session.userId === currentUserId) ??
    (!allowCreateChatSession ? chatSessions[0] : undefined);

  if (fallback) {
    return { kind: "adopt", session: fallback };
  }

  // Nothing to show. Wait while loading; otherwise create (if allowed) or
  // surface the missing state.
  if (loading) {
    return { kind: "pending" };
  }
  if (allowCreateChatSession && !createChatSessionFailed) {
    return { kind: "create" };
  }
  return { kind: "missing" };
}
